import {
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import FormTextField from 'components/ui/form/FormTextField';
import { useFormik } from 'formik';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import FormDateField from 'components/ui/form/FormDateField';
import FormPhoneField from 'components/ui/form/FormPhoneField';
import { motion } from 'framer-motion';
import { stepperItem } from 'utils/motion';
import PhoneFormat from 'components/ui/text-formats/PhoneFormat';
import FormPassportField from 'components/ui/form/FormPassportField';
import { useTranslation } from 'react-i18next';
import FormPatternField from 'components/ui/form/FormPatternField';
import useNotification from 'hooks/useNotification';
import axios from 'axios';
import SimpleSwitchField from 'components/ui/simple-fields/switch/SimpleSwitchField';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import REPAIR_TYPE from 'shared/repairTypeList';
import getLabelByTypeList from 'utils/getLabelByTypeList';
import RESIDENT_TYPE from 'shared/residentTypeList';
import ImagePreviewDialog from 'components/ui/dialogs/ImagePreviewDialog';
import SimpleRadioGroupField from 'components/ui/simple-fields/radio-group/SimpleRadioGroupField';
import {
  citizenshipTypeSelectOptions,
  clientTypeSelectOptions,
} from 'shared/selectOptionsList';
import CITIZENSHIP_TYPE from 'shared/citizenshipTypeList';
import CLIENT_TYPE from 'shared/clientTypeList';
import LeadInfoModal from './LeadInfoModal';
import { useQuery } from 'react-query';
import SimpleToggleButtonGroup from 'components/ui/simple-fields/toggle-button-group/SimpleToggleButtonGroup';
import FormNumberField from 'components/ui/form/FormNumberField';
import FormAutocompleteField from 'components/ui/form/FormAutocompleteField';
import FormNumberTextField from 'components/ui/form/FormNumberTextField';

const validationSchema = yup.object({
  name: yup.string().required('order.modal.add.validation.name'),
  middlename: yup.string().optional(),
  surname: yup.string().required('order.modal.add.validation.surname'),
  phone: yup
    .string()
    .length(17, 'order.modal.add.validation.phoneValid')
    .required('order.modal.add.validation.phone'),
});

const physicalValidationSchema = yup.object({
  name: yup.string().required('client.addEdit.validation.physical.name'),
  middlename: yup
    .string()
    .required('client.addEdit.validation.physical.middleName'),
  surname: yup.string().required('client.addEdit.validation.physical.surname'),
  phone: yup
    .string()
    .length(17, 'client.addEdit.validation.physical.phoneValid')
    .required('client.addEdit.validation.physical.phone'),
  phone2: yup
    .string()
    .length(17, 'client.addEdit.validation.physical.phone2Valid')
    .nullable()
    .optional(),
  passport_series: yup
    .string()
    .trim()
    .min(10, 'client.addEdit.validation.physical.passportSeriesFormat')
    .max(10, 'client.addEdit.validation.physical.passportSeriesFormat')
    .required('client.addEdit.validation.physical.passportSeries'),
  issue: yup
    .date()
    .nullable()
    .typeError('client.addEdit.validation.physical.issueValid')
    .required('client.addEdit.validation.physical.issue'),
  authority: yup
    .string()
    .required('client.addEdit.validation.physical.authority'),
  birthday: yup
    .date()
    .nullable()
    .typeError('client.addEdit.validation.physical.birthdayValid')
    .required('client.addEdit.validation.physical.birthday'),
  work_place: yup.string().optional(),
});

const legalValidationSchema = yup.object({
  name: yup.string().required('client.addEdit.validation.legal.name'),
  phone: yup
    .string()
    .length(17, 'client.addEdit.validation.legal.phoneValid')
    .required('client.addEdit.validation.legal.phone'),
  phone2: yup
    .string()
    .length(17, 'client.addEdit.validation.legal.phone2Valid')
    .nullable()
    .optional(),
  region_id: yup.string().required('client.addEdit.validation.legal.region'),
  city: yup.string().required('client.addEdit.validation.legal.city'),
  home: yup.string().required('client.addEdit.validation.legal.address'),
  inn: yup
    .string()
    .trim()
    .min(9, 'client.addEdit.validation.legal.tinFormat')
    .max(9, 'client.addEdit.validation.legal.tinFormat')
    .optional(),
  mfo: yup.number().required('client.addEdit.validation.legal.mfo'),
  oked: yup.number().required('client.addEdit.validation.legal.oked'),
  account_number: yup
    .string()
    .min(20, {
      label: 'client.addEdit.validation.legal.accountNumberMin',
      value: 20,
    })
    .max(20, {
      label: 'client.addEdit.validation.legal.accountNumberMax',
      value: 20,
    })
    .required('client.addEdit.validation.legal.accountNumber'),
  bank_name: yup.string().required('client.addEdit.validation.legal.bank'),
  director: yup.string().required('settings.companies.validation.director'),
});

const StepOneFromShaxmatka = ({
  appear,
  direction,
  next,
  setData,
  homeData,
  // calculations,
  homesData,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sendNotification = useNotification();
  const axiosPrivate = useAxiosPrivate();
  const [client, setClient] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [clientsList, setClientsList] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsNotFound, setClientsNotFound] = useState(false);
  const [isClientDataMore, setIsClientDataMore] = useState(true);
  const [planLink, setPlanLink] = useState('');
  const [openHomeLayoutImageDialog, setOpenHomeLayoutImageDialog] =
    useState(false);
  const [isClientSearching, setIsClientSearching] = useState(false);
  const [citizenship, setCitizenship] = useState(
    CITIZENSHIP_TYPE.UZBEKISTAN.code,
  );
  const [clientType, setClientType] = useState(CLIENT_TYPE.PHYSICAL.code);
  // Mijoz raqami bo'yicha topilgan CRM lid (vositachi = lead.user)
  const [leadInfo, setLeadInfo] = useState(null);
  const [leadModalOpen, setLeadModalOpen] = useState(false);

  const initialValues = {
    name: '',
    middlename: '',
    surname: '',
    phone: '',
  };

  const physicalInitialValues = {
    name: '',
    middlename: '',
    surname: '',
    phone: '',
    phone2: '',
    passport_series: '',
    issue: '',
    expiry: '',
    email: '',
    inn: '',
    pnfl: '',
    authority: '',
    birthday: '',
    region_id: '',
    city: '',
    home: '',
    work_place: '',
  };

  const legalInitialValues = {
    name: '',
    phone: '',
    phone2: '',
    region_id: '',
    city: '',
    home: '',
    inn: '',
    mfo: '',
    oked: '',
    account_number: '',
    bank_name: '',
    director: '',
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {},
  });

  const formikPhysicalRef = useRef(null);

  const formikPhysical = useFormik({
    initialValues: physicalInitialValues,
    validationSchema: physicalValidationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const response = await axiosPrivate.post(
          '/admin/custom/customstore',
          JSON.stringify(values),
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (response.data && response.data.status && response.data.data) {
          sendNotification({
            msg: t('order.modal.add.alerts.clientSuccess'),
            variant: 'success',
          });
          setIsSubmitting(false);
          setClient(response.data.data);
          setClientsNotFound(false);
          setCustomerId('');
          // Yangi yaratilgan mijoz raqami bo'yicha ham CRM lidni qidiramiz
          lookupLeadByPhone(values.phone);
        }
      } catch (error) {
        sendNotification({
          msg: error?.response?.data?.message || error?.message,
          variant: 'error',
        });
        setIsSubmitting(false);
      }
    },
  });

  const formikLegal = useFormik({
    initialValues: legalInitialValues,
    validationSchema: legalValidationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const response = await axiosPrivate.post(
          '/admin/custom/legalstore',
          JSON.stringify(values),
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (response.data && response.data.status && response.data.data) {
          sendNotification({
            msg: t('order.modal.add.alerts.clientSuccess'),
            variant: 'success',
          });
          setIsSubmitting(false);
          setClient(response.data.data);
          setClientsNotFound(false);
          setCustomerId('');
          // Yangi yaratilgan mijoz raqami bo'yicha ham CRM lidni qidiramiz
          lookupLeadByPhone(values.phone);
        }
      } catch (error) {
        sendNotification({
          msg: error?.response?.data?.message || error?.message,
          variant: 'error',
        });
        setIsSubmitting(false);
      }
    },
  });

  formikPhysicalRef.current = formikPhysical;

  useEffect(() => {
    let cancelled = false;

    const formatPassportSeries = (raw) => {
      if (!raw) return '';
      const clean = String(raw).toUpperCase().replace(/\s+/g, '');
      if (/^[A-Z]{2}\d{7}$/.test(clean)) {
        return `${clean.slice(0, 2)} ${clean.slice(2)}`;
      }
      return clean;
    };

    const applyPassport = (d) => {
      const fp = formikPhysicalRef.current;
      if (!fp || !d) return;

      const passportSeries = d.passport_series
        ? formatPassportSeries(d.passport_series)
        : fp.values.passport_series;

      fp.setValues({
        ...fp.values,
        name: d.name ? String(d.name).toUpperCase() : fp.values.name,
        surname: d.surname
          ? String(d.surname).toUpperCase()
          : fp.values.surname,
        middlename: d.middlename
          ? String(d.middlename).toUpperCase()
          : fp.values.middlename,
        passport_series: passportSeries,
        issue: d.issue || fp.values.issue,
        // Passport amal qilish muddati — NFC chipida bor, ilova jo'natadi
        expiry: d.expiry || fp.values.expiry,
        authority: d.authority || fp.values.authority,
        birthday: d.birthday || fp.values.birthday,
        pnfl: d.pinfl || d.pnfl || fp.values.pnfl,
      });

      setIsClientDataMore(true);

      sendNotification({
        msg: "Passport ma'lumotlari mobile app orqali qabul qilindi",
        variant: 'success',
      });
    };

    const poll = async () => {
      try {
        const res = await axiosPrivate.get('/staff/customer/pending-passport');
        if (cancelled) return;
        const data = res?.data?.data;
        if (data && Object.keys(data).length > 0) {
          applyPassport(data);
        }
      } catch (e) {
        // sukut: foydalanuvchini har 5sda ogohlantirmaymiz
      }
    };

    const intervalId = setInterval(poll, 5000);
    poll();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClientType = (value) => {
    formikPhysical.resetForm();
    formikLegal.resetForm();
    setClientType(value);
    setClient('');
    setCustomerId('');
    setClientsList([]);
    setClientsNotFound(false);
    setData(null);
  };

  const handleClient = (client) => {
    Array.from(document.getElementsByClassName('client-wrapper')).forEach(
      (item) =>
        item.id != `client-${client.id}` &&
        item.classList.remove('item-selected'),
    );
    document
      .getElementById(`client-${client.id}`)
      .classList.toggle('item-selected');

    if (clientType === CLIENT_TYPE.PHYSICAL.code) {
      isClientDataMore
        ? formikPhysical.setValues(physicalInitialValues)
        : formik.setValues(initialValues);
    } else {
      formikLegal.setValues(legalInitialValues);
    }

    if (customerId == client.id) {
      setCustomerId('');
      setData(null);
      setClient('');
      setLeadInfo(null);
    } else {
      setCustomerId(client.id);
      setClient(client);
      if (clientType === CLIENT_TYPE.PHYSICAL.code) {
        if (isClientDataMore) {
          formikPhysical.setValues({
            name: client?.name?.toUpperCase?.(),
            middlename: client?.middlename?.toUpperCase?.(),
            surname: client?.surname?.toUpperCase?.(),
            phone: client.phone,
            phone2: formikPhysical.values.phone2
              ? formikPhysical.values.phone2
              : client.phone2,
            passport_series: formikPhysical.values.passport_series
              ? formikPhysical.values.passport_series
              : client.passport_series,
            issue: formikPhysical.values.issue
              ? formikPhysical.values.issue
              : client.issue,
            inn: formikPhysical.values.inn
              ? formikPhysical.values.inn
              : client.inn,
            pnfl: formikPhysical.values.pnfl
              ? formikPhysical.values.pnfl
              : client.pnfl,
            authority: formikPhysical.values.authority
              ? formikPhysical.values.authority
              : client.authority,
            birthday: formikPhysical.values.birthday
              ? formikPhysical.values.birthday
              : client.birthday,
            region_id: formikPhysical.values.region_id
              ? formikPhysical.values.region_id
              : client.region_id,
            city: formikPhysical.values.city
              ? formikPhysical.values.city
              : client.city,
            home: formikPhysical.values.home
              ? formikPhysical.values.home
              : client.home,
            work_place: formikPhysical.values.work_place
              ? formikPhysical.values.work_place
              : client.work_place,
          });
        } else {
          formik.setValues({
            name: client.name,
            middlename: client.middlename,
            surname: client.surname,
            phone: client.phone,
          });
        }
      } else {
        formikLegal.setValues({
          name: client?.name,
          phone: client?.phone,
          phone2: client?.phone2,
          region_id: client?.detail?.region_id,
          city: client?.detail?.city,
          home: client?.detail?.home,
          inn: client?.detail?.inn,
          mfo: client?.detail?.mfo,
          oked: client?.detail?.oked,
          account_number: client?.detail?.account_number,
          bank_name: client?.detail?.bank_name,
          director: client?.detail?.director,
        });
      }
      let newClient = {
        ...client,
        ...client?.detail,
        id: client?.detail?.custom_id,
      };
      if (newClient.hasOwnProperty('detail')) {
        delete newClient.detail;
      }
      // Oldin topilgan lid bo'lsa vositachisini darrov qo'shamiz;
      // pastdagi qidiruv yangilasa, useEffect qayta merge qiladi
      setData({ ...newClient, lead_operator_id: leadInfo?.user?.id || null });
      // Tanlangan mijoz raqami bo'yicha CRM lidni qidiramiz
      lookupLeadByPhone(client.phone);
    }
  };

  // Mijoz raqami bo'yicha CRM lidni qidiradi. Topilsa — lid ma'lumotlarini
  // modalda ko'rsatadi va vositachisi (lead.user) keyingi bosqichga uzatiladi.
  const lookupLeadByPhone = async (phone) => {
    const digits = (phone || '').replace(/\D+/g, '');
    if (digits.length < 7) {
      setLeadInfo(null);
      return;
    }
    try {
      const response = await axiosPrivate.get(
        `/dictionary/lead-by-phone?phone=${encodeURIComponent(phone)}`,
      );
      const lead = response?.data?.data || null;
      setLeadInfo(lead);
      if (lead) {
        setLeadModalOpen(true);
      }
    } catch (e) {
      setLeadInfo(null);
    }
  };

  // Lid topilgach (yoki yo'qolgach) tanlangan mijoz ma'lumotiga
  // vositachini qo'shib qo'yamiz — StepThree operator_id ni avtomatik oladi.
  useEffect(() => {
    setData((prev) =>
      prev ? { ...prev, lead_operator_id: leadInfo?.user?.id || null } : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadInfo]);

  const handleKeyDown = async (event) => {
    if (event.keyCode == 13) {
      setCustomerId('');
      setClientsLoading(true);
      const response = await axiosPrivate.get(
        isClientDataMore
          ? `/dictionary/customs?name=${
              formikPhysical.values.name || ''
            }&surname=${formikPhysical.values.surname || ''}&phone=${
              formikPhysical.values.phone.replace('+', '%2B') || ''
            }
				&phone2=${formikPhysical.values.phone2.replace('+', '%2B') || ''}`
          : `/dictionary/customs?name=${formik.values.name || ''}&surname=${
              formik.values.surname || ''
            }&phone=${formik.values.phone.replace('+', '%2B') || ''}`,
      );
      if (response.data && response.data.status) {
        setClientsList(response.data.data.data);
        setClientsNotFound(true);
      }
      setClientsLoading(false);
      lookupLeadByPhone(
        isClientDataMore ? formikPhysical.values.phone : formik.values.phone,
      );
    }
  };

  const handleChangeMoreInformation = (value) => {
    setIsClientDataMore(value);
    setCustomerId('');
    setClient('');
    setData(null);
    setClientsList([]);
    setClientsNotFound(false);
  };

  const handleSearchClient = () => {
    if (
      formikPhysical.values.passport_series &&
      formikPhysical.values.birthday &&
      !(
        formikPhysical.touched.passport_series &&
        Boolean(formikPhysical.errors.passport_series) &&
        formikPhysical.touched.birthday &&
        Boolean(formikPhysical.errors.birthday)
      )
    ) {
      setIsClientSearching(true);
      axios
        .get(
          `https://backdemo.smartdiller.com/api/takedata?passport_series=${formikPhysical.values.passport_series}&birthday=${formikPhysical.values.birthday}`,
        )
        .then((response) => {
          if (response && response.data && response.data.length > 0) {
            let dataJSON = response.data[0];
            if (
              dataJSON &&
              dataJSON?.data?.info &&
              dataJSON?.data?.info &&
              dataJSON?.data?.info?.data
            ) {
              const region_id =
                data?.find(
                  (reg) => reg?.code == dataJSON?.data?.passport?.region_id,
                )?.id ||
                formikPhysical.values.region_id ||
                '';
              const address = dataJSON?.data?.passport?.address?.split(' ');
              const city =
                address?.length > 1
                  ? address?.[0] + ' ' + address?.[1]
                  : formikPhysical.values.region_id || '';
              const newValues = {
                region_id,
                name: dataJSON?.data?.info?.data?.name?.toUpperCase?.() || '',
                middlename:
                  dataJSON?.data?.info?.data?.patronymic_name?.toUpperCase?.() ||
                  '',
                surname:
                  dataJSON?.data?.info?.data?.sur_name?.toUpperCase?.() || '',
                phone: formikPhysical.values.phone || '',
                phone2: formikPhysical.values.phone2 || '',
                passport_series: formikPhysical.values.passport_series || '',
                issue: dataJSON?.data?.info?.data?.given_date || null,
                authority: dataJSON?.data?.info?.data?.given_place || '',
                birthday: formikPhysical.values.birthday || '',
                city,
                home: dataJSON?.data?.info?.data?.living_place
                  ? dataJSON?.data?.info?.data?.living_place
                  : dataJSON?.data?.info?.data?.address,
                work_place: formikPhysical.values.work_place || '',
                inn: dataJSON?.data?.info?.data?.tin || '',
                pnfl: dataJSON?.data?.info?.data?.pinfl || '',
              };
              formikPhysical.setValues(newValues);
            }
          }
        })
        .catch((err) => {})
        .finally(() => setIsClientSearching(false));
    } else {
    }
  };

  const { data } = useQuery({
    queryKey: '/dictionary/regions',
    queryFn: async () => {
      const response = await axiosPrivate.get('/dictionary/regions');
      return response.data.data;
    },
    retry: false,
  });

  const { data: appInterfaceData } = useQuery({
    queryKey: '/interface',
    queryFn: async () => {
      const response = await axiosPrivate.get('/interface');
      return response.data?.data;
    },
    retry: false,
  });

  useEffect(() => {
    if (!appInterfaceData) return;
    const fp = formikPhysicalRef.current;
    if (!fp) return;

    const next = { ...fp.values };
    let changed = false;
    if (!fp.values.region_id && appInterfaceData.region_id) {
      next.region_id = appInterfaceData.region_id;
      changed = true;
    }
    if (!fp.values.city && appInterfaceData.city) {
      next.city = appInterfaceData.city;
      changed = true;
    }
    if (changed) fp.setValues(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appInterfaceData]);

  const handleNextStep = () => {
    next();
  };

  return (
    <motion.div
      variants={stepperItem({
        direction: direction,
        duration: 0,
      })}
      initial='hidden'
      animate={appear ? 'show' : 'hidden'}
    >
      <div className='component-add-wrapper'>
        <div className='component-add-body'>
          <div
            className={`order-home-modal-wrapper${
              isClientDataMore || clientType === CLIENT_TYPE.LEGAL.code
                ? ' px-20'
                : ' px-40'
            }`}
          >
            <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
              <Grid
                item={true}
                md={
                  isClientDataMore || clientType === CLIENT_TYPE.LEGAL.code
                    ? 8
                    : 5
                }
                sm={6}
                xs={12}
              >
                {isClientDataMore ? (
                  <SimpleToggleButtonGroup
                    delay={0}
                    value={clientType}
                    changeFn={handleClientType}
                    options={clientTypeSelectOptions}
                  />
                ) : (
                  ''
                )}

                {clientType === CLIENT_TYPE.PHYSICAL.code ? (
                  <>
                    <SimpleSwitchField
                      delay={0}
                      label={t('common.fields.moreInformation')}
                      value={isClientDataMore}
                      setValue={handleChangeMoreInformation}
                      options={[
                        { value: true, checked: true },
                        { value: false, checked: false },
                      ]}
                    />

                    {isClientDataMore ? (
                      <form onSubmit={formikPhysical.handleSubmit}>
                        <Grid container spacing={2} rowSpacing={1} columns={12}>
                          <Grid item={true} sm={12} xs={12}>
                            <Grid
                              container
                              spacing={{ xs: 2, sm: 3, lg: 3 }}
                              rowSpacing={1}
                              columns={{ xs: 12, sm: 12, lg: 12 }}
                            >
                              <Grid item={true} sm={12} xs={12}>
                                <SimpleRadioGroupField
                                  delay={0}
                                  name='citizenship'
                                  label={t('common.fields.citizenship')}
                                  value={citizenship}
                                  changeFn={(value) => setCitizenship(value)}
                                  options={citizenshipTypeSelectOptions}
                                  className='flex'
                                />
                              </Grid>
                              <Grid item={true} lg={4} sm={6} xs={12}>
                                {citizenship ==
                                CITIZENSHIP_TYPE.UZBEKISTAN.code ? (
                                  <FormPassportField
                                    delay={0.1}
                                    label={t('common.fields.passportSeries')}
                                    fieldName='passport_series'
                                    formik={formikPhysical}
                                  />
                                ) : (
                                  <FormTextField
                                    delay={0.1}
                                    label={t('common.fields.passportSeries')}
                                    fieldName='passport_series'
                                    formik={formikPhysical}
                                  />
                                )}
                              </Grid>
                              <Grid item={true} lg={4} sm={6} xs={12}>
                                <FormDateField
                                  delay={0.2}
                                  label={t('common.fields.birthday')}
                                  fieldName='birthday'
                                  formik={formikPhysical}
                                />
                              </Grid>
                              <Grid item={true} lg={4} sm={6} xs={12}>
                                {citizenship ==
                                  CITIZENSHIP_TYPE.UZBEKISTAN.code && (
                                  <Button
                                    variant='outlined'
                                    color='info'
                                    onClick={() => handleSearchClient()}
                                    disabled={isClientSearching}
                                    className='!mt-2 !h-10'
                                    fullWidth
                                  >
                                    {isClientSearching ? (
                                      <div className='flex items-center justify-center'>
                                        <CircularProgress
                                          size={17.5}
                                          color='inherit'
                                        />
                                      </div>
                                    ) : (
                                      <div className='flex items-center text-base'>
                                        <i className='bi bi-search mr-1 text-lg' />
                                        {t('client.addEdit.search')}
                                      </div>
                                    )}
                                  </Button>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item={true} sm={12} xs={12}>
                            <div className='mt-2'>
                              <Divider />
                              <div className='text-lg font-medium mt-3 -mb-2'>
                                {t('client.addEdit.clientDetails')}
                              </div>
                            </div>
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormTextField
                              delay={0.3}
                              label={t('common.fields.name')}
                              fieldName='name'
                              upperCase={true}
                              formik={formikPhysical}
                              onKeyDown={handleKeyDown}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormTextField
                              delay={0.4}
                              label={t('common.fields.surname')}
                              fieldName='surname'
                              upperCase={true}
                              formik={formikPhysical}
                              onKeyDown={handleKeyDown}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormTextField
                              delay={0.5}
                              label={t('common.fields.middleName')}
                              fieldName='middlename'
                              upperCase={true}
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormPhoneField
                              delay={0.6}
                              label={t('common.fields.phone')}
                              fieldName='phone'
                              formik={formikPhysical}
                              onKeyDown={handleKeyDown}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormPhoneField
                              delay={0.7}
                              label={t('common.fields.phone2')}
                              fieldName='phone2'
                              formik={formikPhysical}
                              onKeyDown={handleKeyDown}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormPatternField
                              delay={0.8}
                              label={t('common.fields.tin')}
                              fieldName='inn'
                              formik={formikPhysical}
                              format='#########'
                            />
                          </Grid>

                          <Grid item={true} sm={12} xs={12}>
                            <div className='mt-2'>
                              <Divider />
                              <div className='text-lg font-medium mt-3 -mb-2'>
                                {t('client.addEdit.passportDetails')}
                              </div>
                            </div>
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormDateField
                              delay={0.9}
                              label={t('common.fields.issue')}
                              fieldName='issue'
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormDateField
                              delay={0.95}
                              label={t('common.fields.expiry')}
                              fieldName='expiry'
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormTextField
                              delay={1}
                              label={t('common.fields.authority')}
                              fieldName='authority'
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormPatternField
                              delay={1.1}
                              label={t('common.fields.pnfl')}
                              fieldName='pnfl'
                              formik={formikPhysical}
                              format='##############'
                            />
                          </Grid>

                          <Grid item={true} sm={12} xs={12}>
                            <div className='mt-2'>
                              <Divider />
                              <div className='text-lg font-medium mt-3 -mb-2'>
                                {t('client.addEdit.addressDetails')}
                              </div>
                            </div>
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormSimpleSelectField
                              delay={1.2}
                              options={data}
                              fieldName='region_id'
                              label={t('common.fields.region')}
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormTextField
                              delay={1.3}
                              label={t('common.fields.city')}
                              fieldName='city'
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormTextField
                              delay={1.4}
                              label={t('common.fields.home')}
                              fieldName='home'
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormTextField
                              delay={1.5}
                              label={t('common.fields.workPlace')}
                              fieldName='work_place'
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} lg={4} sm={6} xs={12}>
                            <FormTextField
                              delay={1.6}
                              label={t('common.fields.email')}
                              fieldName='email'
                              formik={formikPhysical}
                            />
                          </Grid>

                          <Grid item={true} xs={12}>
                            <div className='w-full flex items-center justify-center'>
                              <Button
                                color='success'
                                variant='contained'
                                type='submit'
                                disabled={isSubmitting || !!customerId}
                              >
                                {isSubmitting && (
                                  <CircularProgress
                                    size={15}
                                    color='inherit'
                                    className='mr-1'
                                  />
                                )}
                                {t('order.modal.add.addClient')}
                              </Button>
                            </div>
                          </Grid>
                        </Grid>
                      </form>
                    ) : (
                      <form>
                        <Grid container spacing={2} rowSpacing={1} columns={12}>
                          <Grid item={true} xs={12}>
                            <FormTextField
                              delay={0}
                              label={t('common.fields.name')}
                              fieldName='name'
                              formik={formik}
                              onKeyDown={handleKeyDown}
                            />
                          </Grid>

                          <Grid item={true} xs={12}>
                            <FormTextField
                              delay={0}
                              label={t('common.fields.surname')}
                              fieldName='surname'
                              formik={formik}
                              onKeyDown={handleKeyDown}
                            />
                          </Grid>

                          <Grid item={true} xs={12}>
                            <FormTextField
                              delay={0}
                              label={t('common.fields.middleName')}
                              fieldName='middlename'
                              formik={formik}
                            />
                          </Grid>

                          <Grid item={true} xs={12}>
                            <FormPhoneField
                              delay={0}
                              label={t('common.fields.phone')}
                              fieldName='phone'
                              formik={formik}
                              onKeyDown={handleKeyDown}
                            />
                          </Grid>
                        </Grid>
                      </form>
                    )}

                    {leadInfo && (
                      <button
                        type='button'
                        onClick={() => setLeadModalOpen(true)}
                        className='mt-2 w-full flex items-center justify-between gap-2 rounded-lg border border-base-color/40 bg-base-color-light/10 px-3 py-2 text-left hover:bg-base-color-light/20'
                      >
                        <span className='flex flex-col'>
                          <span className='text-xs text-gray-500'>
                            {t(
                              'contract.step.one.lead.responsible',
                              "Mas'ul xodim (vositachi)",
                            )}
                          </span>
                          <span className='text-sm font-semibold text-base-color'>
                            <i className='bi bi-person-badge mr-1' />
                            {leadInfo?.user?.name ||
                              t(
                                'contract.step.one.lead.noResponsible',
                                'Belgilanmagan',
                              )}
                          </span>
                        </span>
                        <i className='bi bi-eye text-base-color' />
                      </button>
                    )}

                    {client && isClientDataMore && (
                      <ButtonBase
                        className='client-wrapper'
                        onClick={() => handleClient(client)}
                        id={`client-${client.id}`}
                      >
                        <span className='text-base font-medium'>
                          {client.name} {client.surname} {client.middlename}
                        </span>
                        <span className='text-sm'>
                          <PhoneFormat value={client.phone} />
                        </span>
                      </ButtonBase>
                    )}

                    {clientsLoading ? (
                      <div className='circular-progress-box pt-6 pb-5'>
                        <CircularProgress size={30} />
                      </div>
                    ) : clientsList &&
                      !isClientDataMore &&
                      clientsList.length > 0 ? (
                      clientsList.map((client) => (
                        <ButtonBase
                          className='client-wrapper'
                          id={`client-${client.id}`}
                          key={client.id}
                          onClick={() => handleClient(client)}
                        >
                          <span className='text-base font-medium'>
                            {client.name} {client.surname} {client.middlename}
                          </span>
                          <span className='text-sm'>
                            <PhoneFormat value={client.phone} />
                          </span>
                        </ButtonBase>
                      ))
                    ) : (
                      clientsNotFound && (
                        <div className='mt-6'>
                          <span className='no-data-found-wrapper'>
                            <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                            {t('common.global.noDataFound')}
                          </span>
                        </div>
                      )
                    )}
                  </>
                ) : (
                  <>
                    <form onSubmit={formikLegal.handleSubmit}>
                      <Grid container spacing={2} rowSpacing={1} columns={12}>
                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormTextField
                            delay={0.1}
                            label={t('common.fields.companyName')}
                            fieldName='name'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormPhoneField
                            delay={0.2}
                            label={t('common.fields.phone')}
                            fieldName='phone'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormPhoneField
                            delay={0.3}
                            label={t('common.fields.phone2')}
                            fieldName='phone2'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormTextField
                            delay={0.4}
                            label={t('common.fields.bank')}
                            fieldName='bank_name'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormNumberField
                            delay={0.5}
                            label={t('common.fields.tin')}
                            fieldName='inn'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormNumberField
                            delay={0.6}
                            label={t('common.fields.mfo')}
                            fieldName='mfo'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormNumberField
                            delay={0.7}
                            label={t('common.fields.oked')}
                            fieldName='oked'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormNumberTextField
                            delay={0.8}
                            label={t('common.fields.accountNumber')}
                            fieldName='account_number'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormAutocompleteField
                            delay={0.9}
                            fieldName='region_id'
                            label={t('common.fields.region')}
                            formik={formikLegal}
                            path={'/dictionary/regions'}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormTextField
                            delay={1}
                            label={t('common.fields.city')}
                            fieldName='city'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormTextField
                            delay={1.1}
                            label={t('common.fields.companyAddress')}
                            fieldName='home'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} lg={4} sm={6} xs={12}>
                          <FormTextField
                            delay={1.1}
                            label={t('common.table.director')}
                            fieldName='director'
                            formik={formikLegal}
                          />
                        </Grid>

                        <Grid item={true} xs={12}>
                          <div className='w-full flex items-center justify-center'>
                            <Button
                              color='success'
                              variant='contained'
                              type='submit'
                              disabled={isSubmitting || !!customerId}
                            >
                              {isSubmitting && (
                                <CircularProgress
                                  size={15}
                                  color='inherit'
                                  className='mr-1'
                                />
                              )}
                              {t('order.modal.add.addClient')}
                            </Button>
                          </div>
                        </Grid>
                      </Grid>
                    </form>

                    {client && clientType === CLIENT_TYPE.LEGAL.code && (
                      <ButtonBase
                        className='client-wrapper'
                        onClick={() => handleClient(client)}
                        id={`client-${client.id}`}
                      >
                        <span className='text-base font-medium'>
                          {client.name}
                        </span>
                        <span className='text-sm'>
                          <PhoneFormat value={client.phone} />
                        </span>
                      </ButtonBase>
                    )}
                  </>
                )}
              </Grid>
              <Grid
                item={true}
                md={
                  isClientDataMore || clientType === CLIENT_TYPE.LEGAL.code
                    ? 4
                    : 7
                }
                sm={6}
                xs={12}
              >
                <div className='home-data-wrapper'>
                  <div className='text-xl text-center'>
                    {t('order.modal.add.home.title')}
                  </div>
                  {homesData?.length > 0
                    ? homesData?.map((homeData) => (
                        <Fragment key={homeData?.id}>
                          <div className='home-data-item'>
                            <div className='data-item-title'>
                              {t('order.modal.add.home.blockName')}:
                            </div>
                            <div className='data-item-value'>
                              {homeData?.name}
                            </div>
                          </div>

                          <div className='home-data-item'>
                            <div className='data-item-title'>
                              {t('order.modal.add.home.homeNumber')}:
                            </div>
                            <div className='data-item-value'>
                              {homeData?.number}
                            </div>
                          </div>

                          <div className='home-data-item'>
                            <div className='data-item-title'>
                              {t('order.modal.add.home.rooms')}:
                            </div>
                            <div className='data-item-value'>
                              {homeData?.rooms}
                            </div>
                          </div>

                          <div className='home-data-item'>
                            <div className='data-item-title'>
                              {t('order.modal.add.home.areaAll')}:
                            </div>
                            <div className='data-item-value'>
                              {homeData?.square}
                            </div>
                          </div>

                          <div className='home-data-item'>
                            <div className='data-item-title'>
                              {t('order.modal.add.home.stage')}:
                            </div>
                            <div className='data-item-value'>
                              {homeData?.stage}
                            </div>
                          </div>

                          <div className='home-data-item'>
                            <div className='data-item-title'>
                              {t('order.modal.add.home.residentType')}:
                            </div>
                            <div className='data-item-value'>
                              {homeData.islive &&
                                getLabelByTypeList(
                                  RESIDENT_TYPE,
                                  homeData.islive,
                                )}
                            </div>
                          </div>

                          <div className='home-data-item'>
                            <div className='data-item-title'>
                              {t('order.modal.add.home.repairType')}:
                            </div>
                            <div className='data-item-value'>
                              {homeData.isrepaired?.toString() &&
                                getLabelByTypeList(
                                  REPAIR_TYPE,
                                  homeData.isrepaired,
                                )}
                            </div>
                          </div>

                          <div className='home-data-item !mb-4'>
                            <div className='data-item-title'>
                              {t('order.modal.add.additional')}:
                            </div>
                            <div className='data-item-value flex lg:flex-row flex-col lg:items-center'>
                              <Button
                                color='info'
                                variant='contained'
                                className='!mr-2'
                                disabled={
                                  !(homeData?.plan && homeData?.plan?.link)
                                }
                                onClick={() => {
                                  setPlanLink(homeData?.plan?.link);
                                  setOpenHomeLayoutImageDialog(true);
                                }}
                              >
                                {t('common.button.homePlan')}
                              </Button>
                            </div>
                          </div>
                        </Fragment>
                      ))
                    : null}
                </div>
              </Grid>

              {client && client?.id && (
                <Grid item={true} sm={12} xs={12}>
                  <div className='text-center'>
                    <Button
                      color='success'
                      variant='contained'
                      type='button'
                      disabled={!customerId}
                      onClick={() => handleNextStep()}
                    >
                      {t('common.button.next')}
                    </Button>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>

          {openHomeLayoutImageDialog && planLink && (
            <ImagePreviewDialog
              open={openHomeLayoutImageDialog}
              setOpen={setOpenHomeLayoutImageDialog}
              url={planLink}
            />
          )}

          {/* Mijoz raqami bo'yicha topilgan CRM lid ma'lumotlari */}
          <LeadInfoModal
            open={leadModalOpen}
            onClose={() => setLeadModalOpen(false)}
            lead={leadInfo}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default memo(StepOneFromShaxmatka);
